import { motion } from 'motion/react'
import type React from 'react'
import { useThemeColors } from '@/contexts/ThemeContext'
import { LoadingText, loadingPresets } from '@/lib/loadingAnimations'
import { theme } from '@/lib/theme'

interface MessageProps {
  children: React.ReactNode
  variant?: 'info' | 'success' | 'error' | 'loading'
  centered?: boolean
  style?: React.CSSProperties
}

/**
 * Reusable message component for displaying feedback, loading states, and status messages.
 * Supports different variants with appropriate styling for each type.
 */
export default function Message({
  children,
  variant = 'info',
  centered = true,
  style,
}: MessageProps) {
  const colors = useThemeColors()
  const getVariantStyles = () => {
    switch (variant) {
      case 'success':
        return {
          backgroundColor: colors.successBg,
          border: `1px solid ${colors.successBorder}`,
          color: colors.primaryText,
        }
      case 'error':
        return {
          backgroundColor: colors.errorBg,
          border: `1px solid ${colors.errorBorder}`,
          color: colors.primaryText,
        }
      case 'loading':
        return {
          color: colors.secondaryText,
          fontStyle: 'italic' as const,
        }
      default:
        return {
          color: colors.secondaryText,
        }
    }
  }

  const messageStyles: React.CSSProperties = {
    padding:
      variant === 'success' || variant === 'error'
        ? theme.spacing.md
        : theme.spacing.lg,
    borderRadius:
      variant === 'success' || variant === 'error' ? theme.borderRadius.sm : 0,
    textAlign: centered ? 'center' : 'left',
    fontSize: theme.fontSize.sm,
    fontFamily: theme.fontFamily.inherit,
    ...getVariantStyles(),
    ...style,
  }

  const content =
    variant === 'loading' && typeof children === 'string' ? (
      <LoadingText
        {...loadingPresets.loading(children, colors.secondaryText)}
        fontSize={theme.fontSize.sm}
      />
    ) : (
      children
    )

  return (
    <motion.div
      style={messageStyles}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
    >
      {content}
    </motion.div>
  )
}
